import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import {Modal, FileUpload} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import Papa from 'papaparse';
import {useCsvUploadModal} from '@/hooks/useCsvUploadModal';
import {convertFileToUtf8} from '@/helpers/encoding';
import {
  INVALID_FORMAT_ERROR,
  DUPLICATE_QUESTIONS_ERROR,
  validateCsvHeaders,
  getMissingColumns,
  parseCsvToQuestions,
  findDuplicateQuestions
} from './wofCsvUtils';
import './WofQuestionsUploadModal.scss';

const parseCsvWithPapa = (text, file, languages) =>
  new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          validateCSV(file, results, languages);
          resolve(parseCsvToQuestions(results.data, languages));
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      },
      error: () => reject(new Error('Error reading CSV file'))
    });
  });

const validateCSV = (file, results, languages) => {
  const headers = results.meta.fields || [];

  if (!file.name.toLowerCase().endsWith('.csv')) {
    throw new Error('Invalid file format. Only CSV files are allowed.');
  }

  if (results.data.length === 0) {
    throw new Error('The uploaded file contains no data rows.');
  }

  const headerError = validateCsvHeaders(headers, languages);
  if (headerError) {
    throw new Error(headerError);
  }

  const hasEmptyHeader = headers.includes('');
  const hasExtraValues = results.data.some((row) =>
    Array.isArray(row.__parsed_extra)
  );

  if (hasEmptyHeader || hasExtraValues) {
    throw new Error(INVALID_FORMAT_ERROR);
  }

  const missingValueErrors = [];
  results.data.forEach((row, idx) => {
    const rowNum = idx + 1;
    languages.forEach((lang) => {
      const missingCols = getMissingColumns(row, lang);
      if (missingCols.length > 0) {
        missingValueErrors.push(
          `Row ${rowNum}: ${missingCols.join(', ')} is missing`
        );
      }
    });
  });

  if (missingValueErrors.length > 0) {
    throw new Error(missingValueErrors.join('. '));
  }

  const {exact, fuzzy} = findDuplicateQuestions(results.data, languages);
  if (exact.length > 0 || fuzzy.length > 0) {
    let message = DUPLICATE_QUESTIONS_ERROR;
    if (exact.length > 0) {
      message += `\nExact duplicates: ${exact.join(', ')}`;
    }
    if (fuzzy.length > 0) {
      message += `\nSimilar questions: ${fuzzy.join(', ')}`;
    }
    throw new Error(message);
  }
};

const WofQuestionsUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  onDone,
  onDownloadTemplate,
  existingFileName,
  languages
}) => {
  const {
    uploadError,
    isParsingFile,
    isUploading,
    isUploadComplete,
    uploadedFileName,
    isFileValid,
    handleClose,
    handleFileDelete,
    handleDone,
    validateFile,
    uploadFile
  } = useCsvUploadModal({isOpen, existingFileName});

  const parseFile = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target.result;
          const normalizedText = text
            .replaceAll('\r\n', '\n')
            .replaceAll('\r', '\n');
          parseCsvWithPapa(normalizedText, file, languages)
            .then(resolve)
            .catch(reject);
        };
        reader.onerror = () => reject(new Error('Error reading CSV file'));
        reader.readAsText(file, 'UTF-8');
      }),
    [languages]
  );

  const handleFileSelect = useCallback(
    (file) => {
      if (!file) return;
      validateFile(file, parseFile, convertFileToUtf8);
    },
    [parseFile, validateFile]
  );

  const handleUploadClick = useCallback(() => {
    uploadFile(onUpload);
  }, [uploadFile, onUpload]);

  return (
    <Modal
      isDark={true}
      onCloseModal={() => handleClose(onClose)}
      onClickSecondaryButton={() => handleClose(onClose)}
      onClickPrimaryButton={() => handleDone(onDone, onClose)}
      showModal={isOpen}
      title='Upload Questions'
      text='Upload questions template.'
      primaryButtonText='Done'
      primaryButtonDisabled={!isUploadComplete || isParsingFile || isUploading}
      secondaryButtonText='Cancel'
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={false}
      showPrimaryButton={false}
      className='wof-upload-modal'
      content={
        <div className='flex flex-col gap-4'>
          <FileUpload
            acceptedExtensions='.csv'
            description='Drag and drop your CSV file here (only .csv)'
            label=''
            onUpload={handleFileSelect}
            onUploadDelete={handleFileDelete}
            value={[]}
            multiple={false}
            isDark={true}
            className='!max-w-full'
            containerClassName='!w-full'
            dataTestId='wof-file-upload'
          />

          {!uploadError && isUploading && (
            <div
              className='rounded p-2'
              style={{
                backgroundColor: 'var(--color-background-layer-02)'
              }}>
              <span className='text-[12px] text-[#DADADA]'>Uploading...</span>
            </div>
          )}

          {!uploadError &&
            (isFileValid || isUploadComplete) &&
            uploadedFileName && (
              <div
                className='flex items-center justify-between rounded px-3 py-2'
                style={{
                  backgroundColor: 'var(--color-background-layer-02)'
                }}>
                <div className='flex min-w-0 items-center gap-2'>
                  <span className='material-icons flex-shrink-0 text-[18px] text-[#0069D7]'>
                    description
                  </span>
                  <span className='truncate text-[14px] text-[#DADADA]'>
                    {uploadedFileName}
                  </span>
                </div>

                <button
                  onClick={handleFileDelete}
                  className='ml-2 flex-shrink-0 text-[#0069D7] hover:text-[#0058B5]'
                  aria-label='Remove uploaded file'>
                  <span className='material-icons text-[20px]'>
                    delete_outline
                  </span>
                </button>
              </div>
            )}

          {uploadError && (
            <div className='flex items-start gap-2'>
              <span className='material-icons mt-0.5 flex-shrink-0 text-[18px] text-[var(--color-feedback-error-high-contrast)]'>
                error
              </span>
              <p className='whitespace-pre-wrap text-[13px] text-[var(--color-feedback-error-high-contrast)]'>
                {uploadError}
              </p>
            </div>
          )}

          <div className='mt-2 flex items-center justify-between'>
            <button
              data-testid='download-template-btn'
              onClick={onDownloadTemplate}
              className='flex items-center gap-2 text-[14px] font-medium text-[#0069D7] hover:text-[#0058B5]'>
              <span className='material-icons text-[18px]'>download</span>
              <span>Download Template</span>
            </button>

            <div className='flex items-center gap-3'>
              <button
                data-testid='cancel-btn'
                onClick={() => handleClose(onClose)}
                className='flex h-8 items-center justify-center rounded-full border border-[#0069D7] px-4 text-[14px] font-medium text-[#0069D7] hover:border-[#0058B5] hover:text-[#0058B5]'>
                Cancel
              </button>
              {isUploadComplete ? (
                <button
                  data-testid='done-btn'
                  onClick={() => handleDone(onDone, onClose)}
                  className='flex h-8 items-center justify-center rounded-full bg-[#0069D7] px-4 text-[14px] font-medium text-white hover:bg-[#0058B5] disabled:cursor-not-allowed disabled:bg-[#4a4a4a] disabled:text-[#888] disabled:hover:bg-[#4a4a4a]'>
                  Done
                </button>
              ) : (
                <button
                  data-testid='upload-btn'
                  onClick={handleUploadClick}
                  disabled={!isFileValid || isParsingFile || isUploading}
                  className='flex h-8 items-center justify-center rounded-full bg-[#0069D7] px-4 text-[14px] font-medium text-white hover:bg-[#0058B5] disabled:cursor-not-allowed disabled:bg-[#4a4a4a] disabled:text-[#888] disabled:hover:bg-[#4a4a4a]'>
                  Upload
                </button>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

WofQuestionsUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  onDownloadTemplate: PropTypes.func,
  existingFileName: PropTypes.string,
  languages: PropTypes.array.isRequired
};

WofQuestionsUploadModal.defaultProps = {
  onDone: () => {},
  onDownloadTemplate: () => {},
  existingFileName: ''
};

export default React.memo(WofQuestionsUploadModal);
